import { create } from "zustand";
import { toast } from "sonner";
import { definitionToWire, linkFromWire, linkPatchToWire, linkToWire, leverToWire, nodeFromWire, worldFromWire } from "@/api/adapt";
import { api, ApiError, clearHttpCache, qs, setCsrfToken } from "@/api/http";
import { clearQueries } from "@/api/query";
import type {
	FormulaCheckDto,
	ForecastWriteDto,
	LinkDto,
	MatchMarketPreviewDto,
	NodeDto,
	ScenarioDto,
	SessionDto,
	UserDto,
	WorkspaceSummaryDto,
	WorldviewDto,
	UndoConflictDetails,
	UndoDto,
	WriteDto,
	Zoom,
} from "@/api/types";
import { setGrid, toAbs } from "@/lib/months";
import type { Definition, EventNode, ModelNode, Lever, Link, MetricNode, Worldview } from "./types";

// The app's state: the signed-in user, the workspace, and the worldview as the API last served it.
// Every action is its endpoint (plan §8.12). After a write the worldview is refetched at the new
// revision, and every read keyed on the revision (results, node detail…) follows.

const EMPTY: Worldview = { nodes: {}, links: [], scenarios: [] };

export type NodeInput = {
	kind: "event" | "metric";
	title: string;
	icon?: string;
	question?: string;
	resolution?: { criteria: string; source: string };
	key_dates?: string[];
	hero_date?: string;
	visibility?: "public" | "private";
	unit?: string;
	scale?: "growth" | "level";
	definition?: Definition;
	forecast?: { by: Record<string, number> } | { range: Record<string, [number, number, number]> } | { formula: string };
};

interface State {
	/** `booting` until the session is known. */
	phase: "booting" | "signed-out" | "signed-in";
	user: UserDto | null;
	workspaces: WorkspaceSummaryDto[];
	/** The workspace slug in the URL (`/w/:ws`). */
	ws: string | null;
	world: Worldview;
	/** Null until the worldview has loaded. */
	revision: number | null;
	asOf: string | null;
	loadError: string | null;
	analyst: boolean;
	/** The scenario being viewed and edited across every page (null = Baseline). Client state only. */
	activeScenario: string | null;
	follows: string[];
	/** Whether undo and redo have something to do for this user (`GET /worldview`, refetched after every write). */
	canRedo: boolean;
	canUndo: boolean;
	/** A forecast save the API refused as stale (`409 STALE_FORECAST`): "Use mine" re-runs it with the new version. */
	staleForecast: { node: string; details: unknown; retry: () => Promise<unknown> } | null;
	dismissStaleForecast: () => void;

	bootstrap: () => Promise<void>;
	setSession: (s: SessionDto) => void;
	signOut: () => Promise<void>;
	enterWorkspace: (ws: string) => Promise<void>;
	refresh: (revision?: number) => Promise<void>;

	undo: () => Promise<void>;
	redo: () => Promise<void>;
	installSample: () => Promise<void>;
	/** Install a template (`sample`, `client-concentration`) into this workspace: one change set, undoable. */
	installTemplate: (name: string) => Promise<boolean>;
	/** Undo a whole change set, such as everything Claude did in one MCP session (PRD §11.3). */
	revertChangeset: (id: string) => Promise<UndoDto | null>;
	setAnalyst: (v: boolean) => void;
	setActiveScenario: (scenarioId: string | null) => void;
	toggleFollow: (id: string) => void;

	saveCurve: (id: string, by: Record<string, number>) => Promise<boolean>;
	savePeriods: (id: string, periods: Record<string, number>, zoom: Zoom) => Promise<boolean>;
	saveRange: (id: string, range: Record<string, [number, number, number]>) => Promise<boolean>;
	saveFormula: (id: string, formula: string) => Promise<boolean>;
	setSource: (id: string, source: ModelNode["source"]) => Promise<boolean>;
	matchMarket: (id: string, dryRun: true) => Promise<MatchMarketPreviewDto | null>;
	applyMarketMatch: (id: string) => Promise<boolean>;
	/** The API's formula check for metric `id`, or for a metric not created yet (`null`). */
	checkFormula: (id: string | null, formula: string) => Promise<FormulaCheckDto | null>;
	updateNode: (id: string, patch: NodePatch) => Promise<boolean>;
	addNode: (input: NodeInput, extra?: { pos?: { x: number; y: number }; observed?: { month: string; value: number }[] }) => Promise<string | null>;
	deleteNode: (id: string) => Promise<boolean>;
	connectNode: (
		id: string,
		dir: "driver" | "dependent",
		other: { existing: string } | { new_node: NodeInput },
		pos?: { x: number; y: number },
	) => Promise<{ node: string; link: Link } | null>;

	addLink: (link: Link) => Promise<Link | null>;
	updateLink: (id: string, patch: Partial<Link>) => Promise<boolean>;
	replaceLink: (id: string, link: Link) => Promise<boolean>;
	removeLink: (id: string) => Promise<boolean>;

	addObservation: (id: string, month: string, value: number) => Promise<boolean>;
	importObservations: (id: string, rows: { month: string; value: number }[], opts?: { replace?: boolean }) => Promise<boolean>;
	removeObservation: (id: string, month: string) => Promise<boolean>;
	markHappened: (id: string, month: string | null) => Promise<boolean>;

	setLayout: (nodeId: string, pos: { x: number; y: number }) => void;
	/** Forget saved positions ("Tidy layout"), so the graph lays these nodes out itself. */
	clearLayout: (nodeIds: string[]) => void;

	addScenario: (s: { name: string; description?: string }) => Promise<string | null>;
	updateScenario: (id: string, patch: { name?: string; description?: string | null }) => Promise<boolean>;
	deleteScenario: (scenarioId: string) => Promise<boolean>;
	/** Set a node's value in a scenario (one lever per node per scenario, VB-11). */
	upsertLever: (scenarioId: string, lever: Lever) => Promise<boolean>;
	upsertLeverPeriods: (scenarioId: string, node: string, periods: Record<string, number>, zoom: Zoom) => Promise<boolean>;
	removeLever: (scenarioId: string, node: string) => Promise<boolean>;
}

export type NodePatch = Partial<{
	title: string;
	icon: string;
	question: string;
	resolution: { criteria: string; source: string };
	key_dates: string[];
	hero_date: string | null;
	tags: string[];
	unit: string;
	scale: "growth" | "level";
	definition: Definition | null;
	/** Public → private only; a private node stays private (D11). */
	visibility: "public" | "private";
}>;

/** Show an API error in a toast: validation details as sentences, else the message. */
export function toastError(e: unknown, fallback = "That didn’t work.") {
	if (e instanceof ApiError) {
		if (e.status === 401) return;
		const ms = e.messages;
		toast.error(ms[0] ?? fallback, ms.length > 1 ? { description: ms.slice(1).join(" ") } : undefined);
		return;
	}
	toast.error(fallback);
	console.error(e);
}

let refreshing: Promise<void> | null = null;
let wanted = 0;

function horizonOf(w: WorldviewDto): string | undefined {
	let h: string | undefined;
	for (const n of w.nodes) if (!h || toAbs(n.horizon) > toAbs(h)) h = n.horizon;
	return h;
}

export const useStore = create<State>()((set, get) => {
	const wsPath = (p: string) => `/w/${encodeURIComponent(get().ws ?? "")}${p}`;
	const nodePath = (id: string, p = "") => wsPath(`/nodes/${encodeURIComponent(id)}${p}`);

	/** Local change shown straight away (dragged values stay put); the refetch replaces it. */
	function optimistic(fn: (w: Worldview) => void) {
		const next = structuredClone(get().world);
		fn(next);
		set({ world: next });
	}

	/** Run a write, then refetch at its revision. Errors become toasts and put the server's state back. */
	// Writes go one at a time, so each sees the forecast version the previous one left (`If-Match`).
	let queue: Promise<unknown> = Promise.resolve();
	function write<T>(
		fn: () => Promise<T>,
		opts: { local?: (w: Worldview) => void; retry?: () => Promise<unknown>; staleNode?: string } = {},
	): Promise<T | null> {
		if (get().workspaces.find((w) => w.slug === get().ws)?.role === "viewer") {
			toast("You can view this workspace but not change it.");
			return Promise.resolve(null);
		}
		if (opts.local) optimistic(opts.local);
		const run = async (): Promise<T | null> => {
			try {
				const r = await fn();
				const rev = (r as { revision?: number } | undefined)?.revision;
				await get().refresh(rev);
				return r;
			} catch (e) {
				if (e instanceof ApiError && e.code === "STALE_FORECAST" && opts.retry) {
					// Someone else (often Claude over MCP) saved this forecast since it loaded: ask whose to keep.
					await get().refresh();
					set({ staleForecast: { node: opts.staleNode ?? "", details: e.details, retry: opts.retry } });
					return null;
				}
				toastError(e);
				await get().refresh();
				return null;
			}
		};
		const p = queue.then(run, run);
		queue = p;
		return p;
	}

	const forecastHeaders = (id: string): Record<string, string> => {
		const n = get().world.nodes[id];
		return n ? { "If-Match": String(n.version) } : {};
	};

	return {
		phase: "booting",
		user: null,
		workspaces: [],
		ws: null,
		world: EMPTY,
		revision: null,
		asOf: null,
		loadError: null,
		analyst: false,
		activeScenario: null,
		follows: [],
		canRedo: false,
		canUndo: false,
		staleForecast: null,
		dismissStaleForecast: () => set({ staleForecast: null }),

		async bootstrap() {
			try {
				const s = await api.get<SessionDto>("/auth/session", { etag: false });
				get().setSession(s);
			} catch (e) {
				set({ phase: "signed-out", loadError: e instanceof ApiError ? e.message : "Can’t reach the API." });
			}
		},

		setSession(s) {
			setCsrfToken(s.csrf_token);
			if (!s.user) {
				set({ phase: "signed-out", user: null, workspaces: [] });
				return;
			}
			set({ phase: "signed-in", user: s.user, workspaces: s.workspaces, analyst: s.user.analyst_mode, loadError: null });
		},

		async signOut() {
			try {
				await api.post("/auth/signout");
			} catch {
				/* signed out either way */
			}
			setCsrfToken(null);
			clearHttpCache();
			clearQueries();
			set({ phase: "signed-out", user: null, workspaces: [], ws: null, world: EMPTY, revision: null, activeScenario: null, follows: [] });
		},

		async enterWorkspace(ws) {
			if (get().ws === ws && get().revision !== null) return;
			clearQueries();
			set({ ws, world: EMPTY, revision: null, loadError: null, activeScenario: null, follows: [], canUndo: false, canRedo: false });
			await get().refresh();
			try {
				const f = await api.get<{ handles: string[] }>(wsPath("/follows"), { etag: false });
				set({ follows: f.handles });
			} catch {
				/* following is a convenience */
			}
		},

		async refresh(revision) {
			if (revision !== undefined && get().revision !== null && revision <= get().revision!) return;
			wanted = Math.max(wanted, revision ?? 0);
			if (refreshing) {
				await refreshing;
				if (get().revision !== null && get().revision! >= wanted) return;
			}
			const ws = get().ws;
			if (!ws) return;
			refreshing = (async () => {
				try {
					const w = await api.get<WorldviewDto>(wsPath("/worldview"));
					if (get().ws !== ws) return;
					setGrid(w.as_of, horizonOf(w));
					const world = worldFromWire(w);
					const active = get().activeScenario;
					set({
						world,
						revision: w.revision,
						asOf: w.as_of,
						loadError: null,
						activeScenario: active && world.scenarios.some((s) => s.id === active) ? active : null,
						canUndo: w.can_undo,
						canRedo: w.can_redo,
					});
				} catch (e) {
					if (get().revision === null) set({ loadError: e instanceof ApiError ? e.message : "Can’t load this worldview." });
					else toastError(e);
				} finally {
					refreshing = null;
				}
			})();
			await refreshing;
		},

		async undo() {
			try {
				const r = await api.post<WriteDto>(wsPath("/undo"));
				await get().refresh(r.revision);
			} catch (e) {
				if (e instanceof ApiError && e.code === "NOTHING_TO_UNDO") toast("Nothing to undo");
				else toastError(e);
				await get().refresh();
			}
		},
		async redo() {
			try {
				const r = await api.post<WriteDto>(wsPath("/redo"));
				await get().refresh(r.revision);
			} catch (e) {
				if (e instanceof ApiError && e.code === "NOTHING_TO_REDO") toast("Nothing to redo");
				else toastError(e);
				await get().refresh();
			}
		},
		async installSample() {
			await get().installTemplate("sample");
		},
		async installTemplate(name) {
			return !!(await write(() => api.post<WriteDto>(wsPath(`/templates/${encodeURIComponent(name)}/install`))));
		},
		async revertChangeset(id) {
			try {
				const r = await api.post<UndoDto>(wsPath(`/changesets/${encodeURIComponent(id)}/revert`));
				await get().refresh(r.revision);
				return r;
			} catch (e) {
				if (e instanceof ApiError && e.code === "UNDO_CONFLICT") {
					const lines = Array.isArray(e.details) ? (e.details as UndoConflictDetails).map((d) => d.message) : [];
					toast.error("These changes can’t be undone: something they touched has changed since.", lines.length ? { description: lines.join(" ") } : undefined);
				} else toastError(e);
				await get().refresh();
				return null;
			}
		},
		setAnalyst(analyst) {
			set({ analyst });
			api.patch<{ user: UserDto }>("/me", { analyst_mode: analyst }).then(
				(r) => set({ user: r.user }),
				(e) => {
					set({ analyst: !analyst });
					toastError(e);
				},
			);
		},
		setActiveScenario: (scenarioId) => set({ activeScenario: scenarioId }),
		toggleFollow(id) {
			const on = get().follows.includes(id);
			set({ follows: on ? get().follows.filter((x) => x !== id) : [...get().follows, id] });
			const p = on ? api.del(nodePath(id, "/follow")) : api.put(nodePath(id, "/follow"));
			p.catch((e) => {
				set({ follows: on ? [...get().follows, id] : get().follows.filter((x) => x !== id) });
				toastError(e);
			});
		},

		async saveCurve(id, by) {
			const r = await write(() => api.put<ForecastWriteDto>(nodePath(id, "/forecast"), { by }, { headers: forecastHeaders(id) }), {
				staleNode: id,
				retry: () => get().saveCurve(id, by),
				local: (w) => {
					const n = w.nodes[id] as EventNode | undefined;
					if (n) n.by = by;
				},
			});
			if (r) rememberSeries(id, JSON.stringify(sortedBy(by)), r);
			return !!r;
		},
		async savePeriods(id, periods, zoom) {
			const r = await write(
				() => api.put<ForecastWriteDto>(nodePath(id, `/forecast${qs({ zoom })}`), { in: periods, zoom }, { headers: forecastHeaders(id) }),
				{
					staleNode: id,
					retry: () => get().savePeriods(id, periods, zoom),
				},
			);
			if (r?.forecast.by) rememberSeries(id, JSON.stringify(sortedBy(r.forecast.by)), r);
			return !!r;
		},
		async saveRange(id, range) {
			const r = await write(() => api.put<ForecastWriteDto>(nodePath(id, "/forecast"), { range }, { headers: forecastHeaders(id) }), {
				staleNode: id,
				retry: () => get().saveRange(id, range),
				local: (w) => {
					const n = w.nodes[id] as MetricNode | undefined;
					if (n) n.range = range;
				},
			});
			if (r) rememberSeries(id, JSON.stringify(sortedBy(range)), r);
			return !!r;
		},
		async saveFormula(id, formula) {
			return !!(await write(() => api.put<ForecastWriteDto>(nodePath(id, "/forecast"), { formula }, { headers: forecastHeaders(id) }), {
				staleNode: id,
				retry: () => get().saveFormula(id, formula),
			}));
		},
		async setSource(id, source) {
			return !!(await write(() => api.put<ForecastWriteDto>(nodePath(id, "/forecast"), { source }, { headers: forecastHeaders(id) }), {
				staleNode: id,
				retry: () => get().setSource(id, source),
			}));
		},
		async matchMarket(id) {
			try {
				return await api.post<MatchMarketPreviewDto>(nodePath(id, "/forecast/match-market"), { dry_run: true });
			} catch (e) {
				toastError(e);
				return null;
			}
		},
		async applyMarketMatch(id) {
			const r = await write(() => api.post<ForecastWriteDto>(nodePath(id, "/forecast/match-market"), { dry_run: false }, { headers: forecastHeaders(id) }), {
				staleNode: id,
				retry: () => get().applyMarketMatch(id),
			});
			if (r?.forecast.by) rememberSeries(id, JSON.stringify(sortedBy(r.forecast.by)), r);
			return !!r;
		},
		async checkFormula(id, formula) {
			try {
				return await api.post<FormulaCheckDto>(wsPath("/formula/check"), id === null ? { formula } : { formula, handle: id });
			} catch (e) {
				if (e instanceof ApiError && e.status === 400)
					return { ok: false, error: e.messages.join(" "), refs: [], lag_refs: [], event_refs: [], unknown: [], issues: [], history: [] };
				toastError(e);
				return null;
			}
		},
		async updateNode(id, patch) {
			const body: Record<string, unknown> = { ...patch };
			if (patch.definition !== undefined) body.definition = patch.definition ? definitionToWire(patch.definition) : null;
			return !!(await write(() => api.patch<{ node: NodeDto; revision: number }>(nodePath(id), body)));
		},
		async addNode(input, extra = {}) {
			const body: Record<string, unknown> = { ...input };
			if (input.definition) body.definition = definitionToWire(input.definition);
			const r = await write(() => api.post<{ node: NodeDto; revision: number }>(wsPath("/nodes"), body));
			if (!r) return null;
			const handle = r.node.handle;
			if (extra.observed?.length) await get().importObservations(handle, extra.observed);
			if (extra.pos) get().setLayout(handle, extra.pos);
			// A new node opens in Baseline: no scenario sets it yet (review 7.4).
			set({ activeScenario: null });
			return handle;
		},
		async deleteNode(id) {
			return !!(await write(() => api.del<WriteDto>(nodePath(id))));
		},
		async connectNode(id, dir, other, pos) {
			// A new node carries its first forecast (a chance or range from the dialog): one changeset, one undo.
			const body: Record<string, unknown> =
				"existing" in other
					? { existing: other.existing }
					: { new_node: { ...other.new_node, definition: other.new_node.definition ? definitionToWire(other.new_node.definition) : undefined } };
			const r = await write(() =>
				api.post<{ node: NodeDto; link: LinkDto; created: boolean; revision: number }>(nodePath(id, dir === "driver" ? "/drivers" : "/dependents"), body),
			);
			if (!r) return null;
			const handle = r.node.handle;
			if (pos && r.created) get().setLayout(handle, pos);
			return { node: handle, link: linkFromWire(r.link) };
		},

		async addLink(link) {
			const r = await write(() => api.post<{ link: LinkDto; revision: number }>(wsPath("/links"), linkToWire(link)));
			return r ? linkFromWire(r.link) : null;
		},
		async updateLink(id, patch) {
			return !!(await write(() => api.patch<WriteDto>(wsPath(`/links/${encodeURIComponent(id)}`), linkPatchToWire(patch)), {
				local: (w) => {
					const l = w.links.find((x) => x.id === id);
					if (l) Object.assign(l, patch);
				},
			}));
		},
		async replaceLink(id, link) {
			return !!(await write(() => api.put<WriteDto>(wsPath(`/links/${encodeURIComponent(id)}`), linkToWire(link))));
		},
		async removeLink(id) {
			return !!(await write(() => api.del<WriteDto>(wsPath(`/links/${encodeURIComponent(id)}`)), {
				local: (w) => {
					w.links = w.links.filter((x) => x.id !== id);
				},
			}));
		},

		async addObservation(id, month, value) {
			return !!(await write(() => api.put<WriteDto>(nodePath(id, `/observations/${month}`), { value })));
		},
		async importObservations(id, rows, opts = {}) {
			return !!(await write(() => api.post<WriteDto>(nodePath(id, "/observations/import"), { rows, ...(opts.replace ? { replace: true } : {}) })));
		},
		async removeObservation(id, month) {
			return !!(await write(() => api.del<WriteDto>(nodePath(id, `/observations/${month}`))));
		},
		async markHappened(id, month) {
			return !!(await write(() => api.put<WriteDto>(nodePath(id, "/resolution"), { happened: month })));
		},

		setLayout(nodeId, pos) {
			// Layout is not recorded or undoable, and does not change the revision (plan §8.2).
			optimistic((w) => {
				if (w.nodes[nodeId]) w.nodes[nodeId].pos = pos;
			});
			api.put(nodePath(nodeId, "/position"), { x: Math.round(pos.x), y: Math.round(pos.y) }).catch((e) => toastError(e));
		},
		clearLayout(nodeIds) {
			// Layout only: not recorded or undoable (plan §8.2).
			const saved = nodeIds.filter((id) => get().world.nodes[id]?.pos);
			if (!saved.length) return;
			optimistic((w) => {
				for (const id of saved) if (w.nodes[id]) delete w.nodes[id].pos;
			});
			api.del(wsPath(`/positions${qs({ nodes: saved.join(",") })}`)).catch((e) => {
				toastError(e);
				void get().refresh();
			});
		},

		async addScenario(s) {
			const r = await write(() =>
				api.post<{ scenario: ScenarioDto; revision: number }>(wsPath("/scenarios"), { name: s.name, ...(s.description ? { description: s.description } : {}) }),
			);
			if (!r) return null;
			set({ activeScenario: r.scenario.id });
			return r.scenario.id;
		},
		async updateScenario(id, patch) {
			return !!(await write(() => api.patch<WriteDto>(wsPath(`/scenarios/${encodeURIComponent(id)}`), patch)));
		},
		async deleteScenario(scenarioId) {
			const ok = !!(await write(() => api.del<WriteDto>(wsPath(`/scenarios/${encodeURIComponent(scenarioId)}`))));
			if (ok && get().activeScenario === scenarioId) set({ activeScenario: null });
			return ok;
		},
		async upsertLever(scenarioId, lever) {
			return !!(await write(
				() => api.put<WriteDto>(wsPath(`/scenarios/${encodeURIComponent(scenarioId)}/levers/${encodeURIComponent(lever.node)}`), leverToWire(lever)),
				{
					local: (w) => {
						const sc = w.scenarios.find((x) => x.id === scenarioId);
						if (sc) sc.levers = [...sc.levers.filter((l) => l.node !== lever.node), { ...lever, id: lever.node }];
					},
				},
			));
		},
		async upsertLeverPeriods(scenarioId, node, periods, zoom) {
			return !!(await write(() =>
				api.put<WriteDto>(wsPath(`/scenarios/${encodeURIComponent(scenarioId)}/levers/${encodeURIComponent(node)}`), { in: periods, zoom }),
			));
		},
		async removeLever(scenarioId, node) {
			return !!(await write(() => api.del<WriteDto>(wsPath(`/scenarios/${encodeURIComponent(scenarioId)}/levers/${encodeURIComponent(node)}`)), {
				local: (w) => {
					const sc = w.scenarios.find((x) => x.id === scenarioId);
					if (sc) sc.levers = sc.levers.filter((l) => l.node !== node);
				},
			}));
		},
	};
});

// ─── The exact stated curve a save returned, until the node detail is refetched ─────────────────

const savedSeries = new Map<string, { key: string; write: ForecastWriteDto }>();

function sortedBy<T>(m: Record<string, T>): [string, T][] {
	return Object.keys(m)
		.sort()
		.map((k) => [k, m[k]]);
}

function rememberSeries(id: string, key: string, write: ForecastWriteDto) {
	savedSeries.set(id, { key, write });
}

/** The series a save of exactly these values returned, if any. */
export function savedSeriesFor(id: string, values: Record<string, unknown>): ForecastWriteDto | null {
	const hit = savedSeries.get(id);
	return hit && hit.key === JSON.stringify(sortedBy(values)) ? hit.write : null;
}

/** Same values, whatever the key order. */
export function sameValues(a: Record<string, unknown>, b: Record<string, unknown>): boolean {
	return JSON.stringify(sortedBy(a)) === JSON.stringify(sortedBy(b));
}

/** Everyday term, or both when analyst mode is on (PRD §4.3). */
export function useTerm() {
	const analyst = useStore((s) => s.analyst);
	return (everyday: string, analystTerm?: string) => (analyst && analystTerm ? `${everyday} (${analystTerm})` : everyday);
}

/** The node as the app model, from a wire node (for writes that return one). */
export function adaptNode(n: NodeDto): ModelNode {
	return nodeFromWire(n);
}
